'use client'

import { useTranslations } from 'next-intl'
import { useId } from 'react'

import { LockSmallIcon } from '@/ui/icons/LockSmallIcon'

import S from './NotificationSettings.module.scss'

/** Mandatory email categories, per role (BAT-987, Confluence 76906819 §4). */
export type EmailCategory = 'account' | 'security' | 'moderation' | 'withdrawal'

export const FAN_EMAIL_CATEGORIES: readonly EmailCategory[] = [
  'account',
  'withdrawal'
]

export const CLUB_EMAIL_CATEGORIES: readonly EmailCategory[] = [
  'security',
  'moderation',
  'withdrawal'
]

export interface EmailChannelProps {
  /** The account's address; it is changed in the account settings. */
  email?: string
  categories: readonly EmailCategory[]
}

/**
 * Email is mandatory in the MVP: the address, each category marked as
 * required and the explanation — no switches that would do nothing.
 */
export function EmailChannel({ email, categories }: EmailChannelProps) {
  const t = useTranslations('notifications.settings.email')
  const titleId = useId()

  return (
    <section className={S.group} aria-labelledby={titleId}>
      <h3 id={titleId} className={S.groupTitle}>
        {t('title')}
      </h3>
      <dl className={S.list}>
        <div className={S.listRow}>
          <dt className={S.listTerm}>{t('address')}</dt>
          <dd className={S.address}>{email ?? '—'}</dd>
        </div>
        {categories.map(category => (
          <div key={category} className={S.listRow}>
            <dt className={S.listTerm}>{t(category)}</dt>
            <dd>
              <span className={S.badge}>{t('required')}</span>
            </dd>
          </div>
        ))}
      </dl>
      <p className={S.hint}>{t('hint')}</p>
    </section>
  )
}

/**
 * "Required" with a lock and a switch locked on (Figma 23206:62499). Only a
 * picture of the state: it is not a control, so it is hidden from assistive
 * technology apart from the "Required" text.
 */
export function RequiredLock({ label }: { label: string }) {
  return (
    <span className={S.required}>
      <LockSmallIcon className={S.lockIcon} />
      <span>{label}</span>
      <span className={S.lockedSwitch} aria-hidden="true">
        <span className={S.switchKnob} />
      </span>
    </span>
  )
}

export interface FanEmailChannelProps {
  email?: string
  /** The address is verified (`is_email_verified`). */
  verified?: boolean
}

/**
 * The fan's email channel (Figma 23206:62499): mandatory — account, security
 * and transaction letters — with the address it goes to.
 */
export function FanEmailChannel({ email, verified }: FanEmailChannelProps) {
  const t = useTranslations('notifications.settings.email')
  const tSettings = useTranslations('notifications.settings')
  const titleId = useId()

  return (
    <section className={S.group} aria-labelledby={titleId}>
      <div className={S.groupHead}>
        <div className={S.groupText}>
          <h3 id={titleId} className={S.groupTitle}>
            {t('title')}
          </h3>
          <p className={S.hint}>{t('description')}</p>
        </div>
        <RequiredLock label={tSettings('requiredChannel')} />
      </div>
      <div className={S.addressBox}>
        <div className={S.addressText}>
          <span className={S.addressLabel}>{t('notificationEmail')}</span>
          <span className={S.address}>{email ?? '—'}</span>
        </div>
        {verified ? (
          <span className={S.verified}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="m3.5 8.5 3 3 6-7"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {t('verified')}
          </span>
        ) : null}
      </div>
    </section>
  )
}
